import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import type {
  AnnotationFrameSummary,
  AnnotationSavedFrameIndexSnapshot,
  AnnotationSavedFrameIndexSource,
} from "@carbon6/c6-frontend-design/annotation";
import {
  listAnnotationSetFrames,
  type AnnotationRequestOptions,
  type AnnotationSetFrameIndexView,
  type AnnotationSetFrameMutationReceipt,
  type AnnotationSetFrameSummaryView,
  type AnnotationSetSummaryView,
} from "@/api";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";

type LoadFrameIndex = (
  setUuid: string,
  options: AnnotationRequestOptions,
) => Promise<AnnotationSetFrameIndexView>;

export class RegistryAnnotationSavedFrameSource implements AnnotationSavedFrameIndexSource {
  private readonly listeners = new Set<() => void>();
  private readonly byId = new Map<string, AnnotationFrameSummary>();
  private snapshot: AnnotationSavedFrameIndexSnapshot;
  private loadVersion = 0;

  constructor(
    private readonly identities: AnnotationIdentityRegistry,
    initialSet: AnnotationSetSummaryView | null,
    private readonly loadFrameIndex: LoadFrameIndex = listAnnotationSetFrames,
  ) {
    this.snapshot = {
      set: initialSet === null ? null : { setId: initialSet.uuid, revision: initialSet.revision },
      revision: initialSet?.revision ?? 0,
      orderedFrames: [],
    };
  }

  getSnapshot = (): AnnotationSavedFrameIndexSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getFrameSummary = (frameId: string): AnnotationFrameSummary | null =>
    this.byId.get(frameId) ?? null;

  async load(set: AnnotationSetSummaryView, options: { signal: AbortSignal }): Promise<void> {
    const version = ++this.loadVersion;
    this.replaceSnapshot({
      set: { setId: set.uuid, revision: set.revision },
      revision: set.revision,
      orderedFrames: [],
    });
    if (isAnnotationBundleSet(set)) return;
    const response = await this.loadFrameIndex(set.uuid, { signal: options.signal });
    if (options.signal.aborted || version !== this.loadVersion) return;
    if (response.set_uuid !== set.uuid) throw new Error("annotation frame index set mismatch");
    const orderedFrames = response.ordered_frames.map((frame) => this.toFrameSummary(frame));
    this.replaceSnapshot({
      set: { setId: response.set_uuid, revision: response.set_revision },
      revision: response.revision,
      orderedFrames,
    });
  }

  selectSet(set: AnnotationSetSummaryView | null): void {
    this.loadVersion += 1;
    this.replaceSnapshot({
      set: set === null ? null : { setId: set.uuid, revision: set.revision },
      revision: set?.revision ?? 0,
      orderedFrames: [],
    });
  }

  applyMutation(receipt: AnnotationSetFrameMutationReceipt): void {
    if (this.snapshot.set?.setId !== receipt.set_uuid) {
      throw new Error("annotation mutation receipt set mismatch");
    }
    const frames = new Map(this.byId);
    if (receipt.deleted_frame_id !== null) frames.delete(receipt.deleted_frame_id);
    if (receipt.frame !== null) {
      const frame = this.toFrameSummary(receipt.frame);
      frames.set(frame.frameId, frame);
    }
    const orderedFrames = [...frames.values()].sort((left, right) => {
      const a = this.identities.frame(left.frameId);
      const b = this.identities.frame(right.frameId);
      if (a === null || b === null) return left.frameId.localeCompare(right.frameId);
      return (
        a.camera.localeCompare(b.camera) ||
        a.episode.localeCompare(b.episode) ||
        a.frameIndex - b.frameIndex
      );
    });
    this.replaceSnapshot({
      set: { setId: receipt.set_uuid, revision: receipt.set_revision },
      revision: receipt.saved_frame_index_revision,
      orderedFrames,
    });
  }

  private toFrameSummary(frame: AnnotationSetFrameSummaryView): AnnotationFrameSummary {
    this.identities.bindFrame(frame.frame_id, {
      camera: frame.camera,
      episode: frame.episode,
      frameIndex: frame.frame_index,
    });
    return {
      frameId: frame.frame_id,
      sequenceId: this.identities.sequenceId(frame.episode),
      streamId: this.identities.streamId(frame.camera),
      timestampSeconds: frame.timestamp_seconds,
      revision: frame.revision,
      annotatedTargetIds: frame.annotated_target_ids.map((targetId) => {
        const objId = Number(targetId);
        if (!Number.isInteger(objId) || objId < 0) {
          throw new Error("invalid annotation target identity");
        }
        return this.identities.targetId(objId);
      }),
    };
  }

  private replaceSnapshot(snapshot: AnnotationSavedFrameIndexSnapshot): void {
    this.snapshot = snapshot;
    this.byId.clear();
    for (const frame of snapshot.orderedFrames) this.byId.set(frame.frameId, frame);
    for (const listener of this.listeners) listener();
  }
}
